import type { ToolActivityStep } from "~/components/domain/ai/agent-activity-state";
import { appendToolActivityStep, resolveToolActivityStep } from "~/components/domain/ai/agent-activity-state";
import type { WorkspaceDisposition } from "~/components/domain/assistant/report-artifact-state";
import { GovernedInsightReportCapabilityKeySchema, governedInsightPresentationManifest } from "~/utils/types/governed-insight-presentation";

export type TurnSegment =
  | { id: number; kind: "thought"; text: string; cumulativeText: string }
  | {
      id: number;
      kind: "phase";
      phase: "reviewing_results" | "preparing_attachments";
      label: string;
      state?: "started" | "completed" | "failed" | "stopped";
    }
  | { id: number; kind: "toolBatch"; steps: ToolActivityStep[] };

export interface MessageWork {
  segments: TurnSegment[];
  contentStartedAfterSegmentCount: number | null;
  durationMs: number;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  work?: MessageWork;
  artifactKey?: string;
  actionProposalId?: string;
  disposition?: WorkspaceDisposition;
  commitRejected?: boolean;
  conversationId?: string;
  turnId?: string;
  feedback?: "positive" | "negative";
  feedbackPending?: boolean;
  feedbackError?: string;
  attachments?: Array<{ sourceId: string; fileName: string; sizeBytes: number; previewUrl?: string }>;
}

export type AssistantFeedback = NonNullable<ChatMessage["feedback"]>;

export const nextAssistantFeedback = (current: AssistantFeedback | undefined, requested: AssistantFeedback): AssistantFeedback | null =>
  current === requested ? null : requested;

export interface AssistantTurnAttachment {
  artifactKey?: string;
  actionProposalId?: string;
  disposition?: WorkspaceDisposition;
  commitRejected: boolean;
  conversationId?: string;
  turnId?: string;
}

const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}/i;

const activitySubject = (args: Record<string, unknown>): string | null => {
  for (const key of ["searchTerm", "query", "text"]) {
    const value = args[key];
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0 && trimmed.length <= 60 && !UUID_LIKE.test(trimmed)) {
        return trimmed;
      }
    }
  }
  return null;
};

const baseActivityLabel = (name: string): { label: string; doneLabel: string; title?: string } => {
  const governedCapability = GovernedInsightReportCapabilityKeySchema.safeParse(name);
  if (governedCapability.success) {
    const activity = governedInsightPresentationManifest(governedCapability.data).activity;
    return { label: activity.running, doneLabel: activity.done };
  }
  switch (name) {
    case "get_note_detail":
      return { label: "Reading note details", doneLabel: "Read note details" };
    case "list_record_documents":
      return { label: "Listing stored documents", doneLabel: "Listed stored documents" };
    case "read_record_document":
      return { label: "Reading a stored document", doneLabel: "Read a stored document" };
    case "find_customer_accounts":
      return { label: "Finding customer accounts", doneLabel: "Found customer accounts" };
    case "get_customer_contacts":
      return { label: "Reading customer contacts", doneLabel: "Read customer contacts" };
    case "present_report":
      return { label: "Formatting your table", doneLabel: "Formatted your table" };
    case "transform_report":
      return { label: "Reshaping your report", doneLabel: "Reshaped your report" };
    default:
      return { label: "Working on your request", doneLabel: "Completed a step" };
  }
};

export const activityLabel = (
  name: string,
  args: Record<string, unknown>,
  options?: { selfName?: string },
): { label: string; doneLabel: string; title?: string } => {
  if (name === "search_notes_and_orders") {
    // Same guard as other activity subjects: trimmed, short, and never an id.
    const terms = Array.isArray(args.terms)
      ? args.terms.flatMap(term => {
          const trimmed = typeof term === "string" ? term.trim() : "";
          return trimmed.length > 0 && trimmed.length <= 60 && !UUID_LIKE.test(trimmed) ? [trimmed] : [];
        })
      : [];
    const first = terms[0];
    if (first) {
      const suffix = terms.length > 1 ? ` and ${terms.length - 1} related ${terms.length === 2 ? "word" : "words"}` : "";
      return { label: `Searching notes for "${first}"${suffix}`, doneLabel: `Searched notes for "${first}"${suffix}` };
    }
    return { label: "Searching notes", doneLabel: "Searched notes" };
  }
  const subject = activitySubject(args);

  if (name === "resolve_entity") {
    const selfName = options?.selfName?.trim().toLocaleLowerCase();
    if (subject && selfName && subject.toLocaleLowerCase() === selfName) {
      return { label: "Checking what you can see", doneLabel: "Checked what you can see" };
    }
    return subject
      ? { label: `Looking up “${subject}”`, doneLabel: `Looked up “${subject}”` }
      : { label: "Looking up your information", doneLabel: "Looked up your information" };
  }

  if (name === "find_orders") {
    const orderClass = args.orderClass === "purchase" ? "purchase orders" : args.orderClass === "sales" ? "sales orders" : "orders";
    const statuses = [
      ...(Array.isArray(args.salesStatuses) ? args.salesStatuses : []),
      ...(Array.isArray(args.purchaseStatuses) ? args.purchaseStatuses : []),
    ]
      .filter((value): value is string => typeof value === "string")
      .slice(0, 2)
      .map(value => value.toLocaleLowerCase().replace(/_/g, " "))
      .join(", ");
    const detail = statuses ? ` · ${statuses}` : "";
    return { label: `Scanning ${orderClass}${detail}`, doneLabel: `Scanned ${orderClass}${detail}` };
  }

  const withSubject = (base: { label: string; doneLabel: string; title?: string }) =>
    subject ? { ...base, label: `${base.label} · “${subject}”`, doneLabel: `${base.doneLabel} · “${subject}”` } : base;
  return withSubject(baseActivityLabel(name));
};

export const appendStreamingContent = (currentContent: string, chunk: string): string => `${currentContent}${chunk}`;

export const applyStreamingContent = (messages: ChatMessage[], content: string): ChatMessage[] => {
  const updated = [...messages];
  const lastMessage = updated[updated.length - 1];
  if (lastMessage?.role === "assistant") {
    updated[updated.length - 1] = { ...lastMessage, content };
  }
  return updated;
};

export const attachWorkOnTurnEnd = (messages: ChatMessage[], work: MessageWork, attachment?: AssistantTurnAttachment): ChatMessage[] => {
  const hasWork = work.segments.length > 0;
  if (!hasWork && !attachment) {
    return messages;
  }
  const attached = {
    ...(hasWork ? { work } : {}),
    ...(attachment?.artifactKey ? { artifactKey: attachment.artifactKey } : {}),
    ...(attachment?.actionProposalId ? { actionProposalId: attachment.actionProposalId } : {}),
    ...(attachment?.disposition ? { disposition: attachment.disposition } : {}),
    ...(attachment ? { commitRejected: attachment.commitRejected } : {}),
    ...(attachment?.conversationId ? { conversationId: attachment.conversationId } : {}),
    ...(attachment?.turnId ? { turnId: attachment.turnId } : {}),
  };
  const presentationMessages = attachment?.actionProposalId
    ? messages.map(message => {
        if (message.actionProposalId !== attachment.actionProposalId) {
          return message;
        }
        const { actionProposalId: _detachedProposalId, ...messageWithoutProposal } = message;
        return messageWithoutProposal;
      })
    : messages;
  const lastMessage = presentationMessages[presentationMessages.length - 1];
  if (lastMessage?.role === "assistant") {
    return [...presentationMessages.slice(0, -1), { ...lastMessage, ...attached }];
  }
  return [...presentationMessages, { role: "assistant", content: "", ...attached }];
};

export const formatWorkSummary = (work: MessageWork): string => {
  const seconds = Math.max(1, Math.round(work.durationMs / 1000));
  const stepCount = work.segments.reduce(
    (count, segment) => count + (segment.kind === "toolBatch" ? segment.steps.length : segment.kind === "phase" ? 1 : 0),
    0,
  );
  return stepCount > 0 ? `Worked ${seconds}s · ${stepCount} step${stepCount === 1 ? "" : "s"}` : `Worked ${seconds}s`;
};

export const appendThoughtSegment = (
  segments: TurnSegment[],
  status: string,
  segmentId: number,
  contentHasStarted: boolean,
): TurnSegment[] => {
  const last = segments[segments.length - 1];
  if (!contentHasStarted && last?.kind === "thought" && status.startsWith(last.cumulativeText)) {
    return status === last.cumulativeText ? segments : [...segments.slice(0, -1), { ...last, text: status, cumulativeText: status }];
  }
  const previousThought = [...segments].reverse().find(segment => segment.kind === "thought");
  if (previousThought?.kind === "thought" && status.startsWith(previousThought.cumulativeText)) {
    const suffix = status.slice(previousThought.cumulativeText.length).trim();
    return suffix.length > 0 ? [...segments, { id: segmentId, kind: "thought", text: suffix, cumulativeText: status }] : segments;
  }
  return [...segments, { id: segmentId, kind: "thought", text: status, cumulativeText: status }];
};

/**
 * Minimum length for a live ticker sentence. Filters conversational filler ("Okay, let's go!")
 * without suppressing short working lines; calibrated against the 2026-08 alpha corpus, where
 * substantive thinking sentences averaged ~90 chars.
 */
const TICKER_MIN_CHARS = 20;

/**
 * Minimum dwell for the live ticker label. Rapid reasoning deltas otherwise replace the line
 * faster than it can be read; 1800ms is the mid-band so one sentence can land before the next
 * replaces it (AF-029).
 */
export const TICKER_DWELL_MS = 1800;

const SENTENCE_BOUNDARY = /(?<=[.!?])\s+/;

const SENTENCE_TERMINAL = /[.!?]$/;

/**
 * A reasoning-summary part opens with a markdown bold headline that the server flattens to
 * "Headline: body" (see sanitizeReasoningSummary in stream-transport.ts). Once the ": "
 * separator has streamed, the headline is a complete display unit even while its body is still
 * arriving. Bounded and terminal-free so ordinary sentences never match past their first clause.
 */
const PART_HEADLINE = /^([^.!?\n]{1,120}?):\s/;

/**
 * The complete display units of one thought part, in stream order: the flattened headline (once
 * its separator has streamed) followed by each fully punctuated sentence of the body. A trailing
 * chunk without terminal punctuation is still streaming and never qualifies — promoting it put
 * dangling prefixes like "Evaluating sales opportunities and inventory: I've" on screen for a
 * full dwell window (founder UAT 2026-08-12).
 */
const completedThoughtUnits = (cumulativeText: string): string[] => {
  const text = cumulativeText.trim();
  const headline = PART_HEADLINE.exec(text);
  const units: string[] = [];
  if (headline?.[1] !== undefined) {
    units.push(headline[1].trim());
  }
  const body = headline ? text.slice(headline[0].length) : text;
  for (const chunk of body.split(SENTENCE_BOUNDARY)) {
    const sentence = chunk.trim();
    if (sentence.length > 0 && SENTENCE_TERMINAL.test(sentence)) {
      units.push(sentence);
    }
  }
  return units;
};

/**
 * The one line the live surface shows while the model thinks: the most recent complete unit of
 * the latest thought — its headline or its last finished sentence, never a mid-sentence prefix —
 * falling back through earlier units and segments when the newest is filler or still streaming.
 * Returns null when nothing substantive has completed yet (callers render the generic thinking
 * line instead). The full trail stays available in the expanded trace.
 */
export const latestThoughtTicker = (segments: TurnSegment[]): string | null => {
  for (let index = segments.length - 1; index >= 0; index -= 1) {
    const segment = segments[index];
    if (segment?.kind !== "thought") {
      continue;
    }
    const units = completedThoughtUnits(segment.cumulativeText);
    for (let position = units.length - 1; position >= 0; position -= 1) {
      const unit = units[position];
      if (unit !== undefined && unit.length >= TICKER_MIN_CHARS) {
        return unit;
      }
    }
  }
  return null;
};

export const dwellGatedTickerLabel = ({
  displayedLabel,
  latestLabel,
  lastChangeAtMs,
  nowMs,
  flush,
  dwellMs = TICKER_DWELL_MS,
}: {
  displayedLabel: string | null;
  latestLabel: string | null;
  lastChangeAtMs: number;
  nowMs: number;
  flush: boolean;
  dwellMs?: number;
}): { displayedLabel: string | null; lastChangeAtMs: number; waitMs: number | null } => {
  if (displayedLabel === latestLabel) {
    return { displayedLabel, lastChangeAtMs, waitMs: null };
  }
  if (flush || displayedLabel === null || nowMs - lastChangeAtMs >= dwellMs) {
    return { displayedLabel: latestLabel, lastChangeAtMs: nowMs, waitMs: null };
  }
  return { displayedLabel, lastChangeAtMs, waitMs: dwellMs - (nowMs - lastChangeAtMs) };
};

export const appendPhaseSegment = (
  segments: TurnSegment[],
  segmentId: number,
  event: Extract<import("~/shared/ai/assistant-event-contract").AgentEvent, { type: "phase" }> = {
    type: "phase",
    phase: "reviewing_results",
  },
): TurnSegment[] => {
  if (event.phase === "reviewing_results") {
    return [...segments, { id: segmentId, kind: "phase", phase: event.phase, label: "Reviewing the results…" }];
  }
  const names = event.fileNames.join(", ");
  const label =
    event.state === "started"
      ? `Reading ${names}…`
      : event.state === "completed"
        ? `Read ${names}`
        : event.state === "stopped"
          ? `Stopped reading ${names}`
          : `Could not read ${event.failedFileNames.join(", ")}`;
  if (event.state !== "started") {
    let activeIndex = -1;
    for (let index = segments.length - 1; index >= 0; index -= 1) {
      const segment = segments[index];
      if (segment?.kind === "phase" && segment.phase === "preparing_attachments") {
        activeIndex = index;
        break;
      }
    }
    if (activeIndex >= 0) {
      return segments.map((segment, index) =>
        index === activeIndex ? { id: segment.id, kind: "phase", phase: event.phase, label, state: event.state } : segment,
      );
    }
  }
  return [...segments, { id: segmentId, kind: "phase", phase: event.phase, label, state: event.state }];
};

export const appendToolCallSegment = (
  segments: TurnSegment[],
  entry: Parameters<typeof appendToolActivityStep>[1],
  segmentId: number,
): TurnSegment[] => {
  let correctiveSegmentId: number | null = null;
  let correctiveStepId: number | null = null;
  for (let segmentIndex = segments.length - 1; segmentIndex >= 0 && correctiveSegmentId === null; segmentIndex -= 1) {
    const segment = segments[segmentIndex];
    if (segment?.kind !== "toolBatch") {
      continue;
    }
    for (let stepIndex = segment.steps.length - 1; stepIndex >= 0; stepIndex -= 1) {
      const step = segment.steps[stepIndex];
      if (step?.name === entry.name && step.state === "needs_adjustment") {
        correctiveSegmentId = segment.id;
        correctiveStepId = step.id;
        break;
      }
    }
  }
  const corrected: TurnSegment[] = segments.map(segment =>
    segment.kind === "toolBatch" && segment.id === correctiveSegmentId
      ? { ...segment, steps: segment.steps.map(step => (step.id === correctiveStepId ? { ...step, correctiveCallStarted: true } : step)) }
      : segment,
  );
  const last = corrected[corrected.length - 1];
  if (last?.kind === "toolBatch") {
    return [...corrected.slice(0, -1), { ...last, steps: appendToolActivityStep(last.steps, entry) }];
  }
  return [...corrected, { id: segmentId, kind: "toolBatch", steps: appendToolActivityStep([], entry) }];
};

export const resolveToolResultSegment = (
  segments: TurnSegment[],
  name: string,
  outcome: Parameters<typeof resolveToolActivityStep>[2],
  callId?: string,
): TurnSegment[] => {
  for (let segmentIndex = segments.length - 1; segmentIndex >= 0; segmentIndex -= 1) {
    const segment = segments[segmentIndex];
    if (segment?.kind !== "toolBatch") {
      continue;
    }
    const steps = resolveToolActivityStep(segment.steps, name, outcome, callId);
    if (steps === segment.steps) {
      continue;
    }
    if (steps.length === 0) {
      return segments.filter((_, index) => index !== segmentIndex);
    }
    return segments.map((candidate, index) => (index === segmentIndex ? { ...segment, steps } : candidate));
  }
  return segments;
};

export const failActiveToolSegments = (segments: TurnSegment[]): TurnSegment[] =>
  segments.map(segment =>
    segment.kind === "toolBatch" && segment.steps.some(step => step.state === "active")
      ? { ...segment, steps: segment.steps.map(step => (step.state === "active" ? { ...step, state: "failed" } : step)) }
      : segment,
  );

export const splitTurnSegmentsAroundContent = (
  segments: TurnSegment[],
  contentStartedAfterSegmentCount: number | null,
): { beforeContent: TurnSegment[]; afterContent: TurnSegment[] } => {
  const boundary = contentStartedAfterSegmentCount ?? segments.length;
  return { beforeContent: segments.slice(0, boundary), afterContent: segments.slice(boundary) };
};

export type LiveTurnFrontier = "beforeContent" | "answer" | "afterContent";

export const resolveLiveTurnFrontier = (hasInFlightReply: boolean, hasTrailingWork: boolean): LiveTurnFrontier => {
  if (hasTrailingWork) {
    return "afterContent";
  }
  return hasInFlightReply ? "answer" : "beforeContent";
};

/**
 * What the once-only `initialQuery` effect does with the query on this render: submit it as a
 * turn (arrived via `send=1` from the overlay handoff), prefill the composer (a plain `?q=` link),
 * or nothing (no query, or the effect already ran for this mount).
 */
export const initialQueryDisposition = (
  initialQuery: string | undefined,
  send: boolean,
  alreadyProcessed: boolean,
): "send" | "prefill" | "none" => {
  if (!initialQuery || alreadyProcessed) {
    return "none";
  }
  return send ? "send" : "prefill";
};
